/**
 * services/adminUsersApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin-only user and shop management methods.
 *
 * All methods require an admin token passed as the first argument.
 *
 * METHODS:
 *   Users:
 *     fetchUsers(token)                       — list all users
 *     createUser(userData, token)             — create a user account
 *     updateUser(userId, userData, token)     — update user data
 *     toggleUserStatus(userId, token)         — activate / suspend a user
 *     deleteUser(userId, token)               — permanently delete a user
 *     getUserById(userId, token)              — fetch one user by ID
 *   Logs:
 *     fetchAdminLogs(token, params)           — paginated admin action log
 *     exportAdminLogs(token, format)          — export logs as CSV/JSON
 *     logAdminAction(token, logData)          — write a custom log entry
 *   Admin shops:
 *     fetchAdminShops(token)                  — list all shops (admin view)
 *     updateAdminShop(shopId, data, token)    — update any shop
 *     approveShop(shopId, token)              — approve a pending shop
 *     rejectShop(shopId, token)              — reject a pending shop
 *     deleteAdminShop(shopId, token)          — delete a shop
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async fetchUsers(token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/`, { headers: this._adminHeaders(token) });
      if (!res.ok) throw new Error(`fetchUsers failed: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) return data;
      if (data?.results)       return data.results;
      if (data?.users)         return data.users;
      if (data?.data)          return data.data;
      return [];
    } catch (error) { console.error('❌ fetchUsers error:', error); return []; }
  },

  async createUser(userData, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/`, {
        method: 'POST', headers: this._adminHeaders(token), body: JSON.stringify(userData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to create user' };
      }
      return { success: true, data: await res.json() };
    } catch (error) { return { success: false, error: error.message || 'Network error.' }; }
  },

  async updateUser(userId, userData, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
        method: 'PATCH', headers: this._adminHeaders(token), body: JSON.stringify(userData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to update user' };
      }
      return { success: true, data: await res.json() };
    } catch (error) { return { success: false, error: error.message || 'Network error.' }; }
  },

  async toggleUserStatus(userId, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/toggle-status/`, {
        method: 'POST', headers: this._adminHeaders(token),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to toggle status' };
      }
      return { success: true, data: await res.json() };
    } catch (error) { return { success: false, error: error.message || 'Network error.' }; }
  },

  async deleteUser(userId, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, {
        method: 'DELETE', headers: this._adminHeaders(token),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to delete user' };
      }
      return { success: true };
    } catch (error) { return { success: false, error: error.message || 'Network error.' }; }
  },

  async getUserById(userId, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/`, { headers: this._adminHeaders(token) });
      if (!res.ok) throw new Error(`getUserById failed: ${res.status}`);
      return await res.json();
    } catch (error) { console.error('❌ getUserById error:', error); return null; }
  },
// ADMIN — ACTIVITY LOGS

  async fetchAdminLogs(token, params = {}) {
    try {
      const qs = new URLSearchParams();
      if (params.limit)    qs.append('limit',    params.limit);
      if (params.offset)   qs.append('offset',   params.offset);
      if (params.action)   qs.append('action',   params.action);
      if (params.admin_id) qs.append('admin_id', params.admin_id);
      const url = `${API_BASE_URL}/admin/dashboard/activity/${qs.toString() ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: this._adminHeaders(token) });
      if (!res.ok) throw new Error(`fetchAdminLogs failed: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) return data;
      if (data?.results)       return data.results;
      if (data?.logs)          return data.logs;
      if (data?.data)          return data.data;
      return [];
    } catch (error) { console.error('❌ fetchAdminLogs error:', error); return []; }
  },

  async exportAdminLogs(token, format = 'csv') {
    try {
      const logs = await this.fetchAdminLogs(token, { limit: 1000 });
      if (!logs.length) return { success: false, error: 'No logs to export' };
      const headers = ['id','type','title','user','description','timestamp'];
      const csvRows = [
        headers.join(','),
        ...logs.map(l => headers.map(h => JSON.stringify(l[h] ?? '')).join(',')),
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `admin-activity-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true };
    } catch (error) { return { success: false, error: error.message || 'Failed to export logs' }; }
  },

  async logAdminAction(token, logData) {
    console.log('📝 logAdminAction (no-op):', logData);
    return { success: true };
  },
// ADMIN — SHOPS

  async fetchAdminShops(token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/shops/`, { headers: this._adminHeaders(token) });
      if (res.status === 404) {
        const fallback = await fetch(`${API_BASE_URL}/shops/`, { headers: this._adminHeaders(token) });
        if (!fallback.ok) return [];
        const d = await fallback.json();
        return Array.isArray(d) ? d : (d?.results || []);
      }
      if (!res.ok) throw new Error(`fetchAdminShops failed: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) return data;
      if (data?.results)       return data.results;
      if (data?.shops)         return data.shops;
      if (data?.data)          return data.data;
      return [];
    } catch (error) { console.error('❌ fetchAdminShops error:', error); return []; }
  },

  async updateAdminShop(shopId, shopData, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/shops/${shopId}/`, {
        method: 'PATCH', headers: this._adminHeaders(token), body: JSON.stringify(shopData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || err.error || 'Failed to update shop' };
      }
      return { success: true, data: await res.json() };
    } catch (error) { return { success: false, error: error.message || 'Network error' }; }
  },

  async approveShop(shopId, token) {
    return this.updateAdminShop(shopId, { is_verified: true, is_active: true }, token);
  },

  async rejectShop(shopId, token) {
    return this.updateAdminShop(shopId, { is_verified: false, is_active: false }, token);
  },

  async deleteAdminShop(shopId, token) {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/shops/${shopId}/`, {
        method: 'DELETE', headers: this._adminHeaders(token),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || err.error || 'Failed to delete shop' };
      }
      return { success: true };
    } catch (error) { return { success: false, error: error.message || 'Network error' }; }
  }

  // ADMIN — PRODUCTS (validity adjustment)
};

